/** @jsx jsx */
import React from 'react';
import moment from 'moment';
import { css, jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import TabControl from 'components/atoms/TabControl';
import Table from 'components/molecules/Table';
import { IAllowanceChangeLog } from './interface';

/**
 * 手当申請の更新履歴モーダル（C3_RESDUC-1877）
 *
 * 手本: ApprovedTab/StampAccordion.tsx の「更新履歴」モーダル（打刻修正申請）
 * 相違: 明細が打刻（開始・終了・業務）ではなく手当（手当名・申請値・備考）。
 *       申請中／承認済／否決済の 3 タブで同じものを出すため、手本のようにタブごとに複製せず 1 つにまとめている。
 */
const statusName = (status: number): string => {
  if (status === 0) return '申請中';
  if (status === 1) return '承認';
  if (status === 2) return '否決';
  return '';
};

const AllowanceChangeLogModal: React.FC<{
  open: boolean;
  data: IAllowanceChangeLog[];
  selected: string;
  setSelected: (value: string) => void;
  onClose: () => void;
}> = ({
  open, data, selected, setSelected, onClose,
}) => {
  const log = data[Number(selected)];
  const isRejected = !!log && log.applicationStatus === 2;

  return (
    <div
      css={css`
        > div > div { max-width: 600px; }
        /* 承認一覧のアコーディオンが table th:nth-child(n) で自分の表の列幅を指定しており、
           その中に描かれるこの表にも同じ指定が掛かる（要承認タブは 1 列目がチェックボックス用の 20px）。
           手当名が潰れて改行されるため、ここで列幅を上書きする。!important は親の指定に勝つために必要 */
        table th:nth-child(1) { width: 47% !important; }
        table th:nth-child(2) { width: 18% !important; }
        table th:nth-child(3) { width: 35% !important; }
      `}
    >
      <Modal
        open={open}
        closeHandler={onClose}
        title="更新履歴"
        submitText="閉じる"
        onSubmit={onClose}
        isShowButtonClose={false}
      >
        <TabControl
          items={data.map((item, index) => ({
            value: String(index),
            display: `ver${item.version}`,
          }))}
          setValue={setSelected}
          currentValue={selected}
          isFixed={true}
        />
        <div>
          {log && (
            <React.Fragment>
              <div>{`申請種別: ${log.modifiyStatus === 1 ? '手当削除申請' : '手当申請'}`}</div>
              <div>{`状態: ${statusName(log.applicationStatus)}`}</div>
              <div>
                {log.applicationStaffCode
                  ? `申請者: ${log.applicationStaffCode} ${log.applicationStaffName || ''}`
                  : null}
              </div>
              <div>
                {log.applicationDate
                  ? `申請日時: ${moment(log.applicationDate).format('YYYY/MM/DD HH:mm:ss')}`
                  : null}
              </div>
              <div>{`出勤店舗: ${log.orgCode} ${log.orgName || ''}`}</div>

              <Table>
                <thead>
                  <tr>
                    {/* 列幅は上の wrapper の CSS で指定（ここに width を書いても親の nth-child 指定に負ける）。
                        HeaderCell は padding なし・Cell は 2px 4px なので、ヘッダーにも同じ余白を付けて文字の起点をそろえる */}
                    <Table.HeaderCell customStyle={css({ padding: '2px 4px' })}>手当名</Table.HeaderCell>
                    <Table.HeaderCell customStyle={css({ padding: '2px 4px' })}>申請値</Table.HeaderCell>
                    <Table.HeaderCell customStyle={css({ padding: '2px 4px' })}>備考</Table.HeaderCell>
                  </tr>
                </thead>
                <tbody>
                  {(log.detailList || []).map((detail) => (
                    <tr key={detail.allowanceId}>
                      <Table.Cell>{detail.allowanceName}</Table.Cell>
                      <Table.Cell>
                        {`${Number(detail.allowanceValue || 0).toLocaleString()}${detail.dispUnit || ''}`}
                      </Table.Cell>
                      <Table.Cell>{detail.comment || ''}</Table.Cell>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div>{log.applicationReason ? `申請理由: ${log.applicationReason}` : null}</div>
              {isRejected && log.rejectReason && (
                <div>{`否決コメント: ${log.rejectReason}`}</div>
              )}
              {log.approvalDate && (
                <div>
                  {`${isRejected ? '否決日時' : '承認日時'}: ${moment(log.approvalDate).format('YYYY/MM/DD HH:mm:ss')}`}
                </div>
              )}
              {log.approvalStaffCode && (
                <div>
                  {`${isRejected ? '否決者' : '承認者'}: ${log.approvalStaffCode} ${log.approvalStaffName || ''}`}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AllowanceChangeLogModal;
